import React from "react";
import { generatePath } from "react-router-dom";
import { ActionListItemDescriptor, Card, EmptyState, Heading, Stack } from "@shopify/polaris";
import styled from "styled-components";

import CompanyIcon from "../../../../assets/icons/company.svg";
import ExistingCustomersIcon from "../../../../assets/icons/lines-horizontal-3.svg";
import PersonIcon from "../../../../assets/icons/person.svg";
import PlusMajorIcon from "../../../../assets/icons/plus-major.svg";
import api from "../../../api";
import ActionsDropdown from "../../../components/ActionsDropdown";
import CustomersListTable from "../../../components/CustomersListTable";
import { ROUTES } from "../../../constants/routes";
import useFormatMessage from "../../../hooks/useFormatMessage";
import useOpenClose from "../../../hooks/useOpenClose";
import useSelectionMap from "../../../hooks/useSelectionMap";
import useSetLocationState from "../../../hooks/useSetLocationState";
import { Customer } from "../../../types/utilities";

import AddCustomersToProjectModal from "./AddCustomersToProjectModal";
import ProjectCustomersBulkActions from "./ProjectCustomersBulkActions";

interface ProjectCustomersSectionProps {
  project: api.Project;
  readonly?: boolean;
}

const ProjectCustomersSection: React.FC<ProjectCustomersSectionProps> = (props) => {
  const { project, readonly } = props;

  const [showCustomersSelectModal, toggleCustomersSelectModal, closeCustomersSelectModal] = useOpenClose();
  const [selectedCustomersIds, selectedCustomers, handleSelectionChange, clearSelection] = useSelectionMap<Customer>(
    project.customers
  );
  const f = useFormatMessage();

  const disabled = readonly || project.status !== "ACTIVE";
  const count = project.customers.length;

  /*
  onboarding links from the project details page should redirect the user
  to the onboarding pages for person or company and associate the new customers-section with this project.
  they should also display the project name.

  in order to associate the new customers-section with this project, the project id should be added to the
  api.createPersonCustomer or api.updateCompanyScreeningConfiguration.

  the mechanism to pass data between links is by using react router's link state object.
  when setting data in a link state, after navigating to that link (from within the application) the component
  rendered from that link can access the link's state and retrieve the information stored in it.

  the component that renders links is AdapterLink which is set as the Link component renderer in App.tsx:
  <AppProvider linkComponent={AdapterLink} ... />

  inside AdapterLink, the project id and name are retrieved from the current location state (the project details URL)
  and added to the created links state - the created links are for the onboarding of new customers-section.
*/
  useSetLocationState("createdFromProject", { id: project.id, name: project.name });

  const companyOnboardingPath = generatePath(ROUTES.COMPANY_LOOKUP);
  const personOnboardingPath = generatePath(ROUTES.PERSON_ONBOARDING_NEW);

  const items: ActionListItemDescriptor[] = [
    {
      content: f("project.customers.buttons.add-existing-customers"),
      onAction: () => toggleCustomersSelectModal(),
      disabled: readonly,
      image: ExistingCustomersIcon,
    },
    {
      content: f("project.customers.buttons.add-new-person-customer"),
      url: personOnboardingPath,
      disabled: readonly,
      image: PersonIcon,
    },
    {
      content: f("project.customers.buttons.add-new-company-customer"),
      url: companyOnboardingPath,
      disabled: readonly,
      image: CompanyIcon,
    },
  ];

  const isAllSelected = count === selectedCustomersIds.length;
  const selectedLabel = isAllSelected
    ? f("customers.list.header.customers.count.all", { count })
    : f("customers.list.header.customers.count", { count, selected: selectedCustomersIds.length });

  return (
    <Stack vertical>
      <Stack alignment="trailing">
        <Stack vertical spacing={count === 0 ? "none" : "loose"}>
          <Heading>{f("project.customers.title")}</Heading>
          <Stack alignment="center">
            {count > 0 && <StyledTableSelection>{selectedLabel}</StyledTableSelection>}
            {selectedCustomersIds.length > 0 && (
              <ProjectCustomersBulkActions
                project={project}
                customers={selectedCustomers}
                readonly={readonly}
                onClearSelection={clearSelection}
              />
            )}
          </Stack>
        </Stack>
        <Stack.Item fill />
        <ActionsDropdown
          items={items}
          icon={PlusMajorIcon}
          title={f("project.customers.buttons.add-customers")}
          disabled={disabled}
          size="slim"
        />
      </Stack>
      <Card>
        {count > 0 ? (
          <CustomersListTable
            isLoading={false}
            hidePagination
            customers={project.customers}
            count={count}
            sortable
            sortLocally
            selectable
            selectedIds={selectedCustomersIds}
            onSelectionChange={handleSelectionChange}
          />
        ) : (
          <Card.Section>
            <EmptyState heading={f("project.customers.empty.header")} image={""}>
              <p>{f("project.customers.empty.message")}</p>
            </EmptyState>
          </Card.Section>
        )}
      </Card>
      {showCustomersSelectModal && (
        <AddCustomersToProjectModal project={project} readonly={readonly} onClose={closeCustomersSelectModal} />
      )}
    </Stack>
  );
};

const StyledTableSelection = styled.p`
  min-height: 3rem;
  line-height: 3rem;
`;

export default ProjectCustomersSection;
